/**
 * DEGAP session orchestrator.
 * Manages the full DEGAP flow: Decide, Engage, Gather, Arrange, Prioritize.
 */

import type {
  Item,
  CanonicalScoreSet,
} from "../harmonizer/types";
import { ArrangeSession } from "../arrange/store";

/**
 * Outcome driver for the Decide phase.
 */
export type OutcomeDriver = {
  id: string;
  name: string;
  description?: string;
  priority?: number;
};

/**
 * Participant for the Engage phase.
 */
export type Participant = {
  id: string;
  name: string;
  email?: string;
  role?: string;
};

/**
 * Full DEGAP session managing all phases of the prioritization workflow.
 */
export class DEGAPSession {
  decide: { outcomeDrivers: OutcomeDriver[] };
  engage: { participants: Participant[] };
  gather: { items: Item[] };
  arrange: ArrangeSession;
  prioritize: { finalSnapshot: CanonicalScoreSet | null };

  /**
   * Creates a new DEGAP session.
   * 
   * @param initialItems - Initial items for the Gather phase
   */
  constructor(initialItems: Item[] = []) {
    this.decide = { outcomeDrivers: [] };
    this.engage = { participants: [] };
    this.gather = { items: initialItems };
    this.arrange = new ArrangeSession(initialItems, null, {}, null);
    this.prioritize = { finalSnapshot: null };
  }

  /**
   * Records an outcome driver in the Decide phase.
   * 
   * @param driver - Outcome driver to record
   */
  recordOutcomeDriver(driver: OutcomeDriver): void {
    this.decide.outcomeDrivers.push(driver);
  }

  /**
   * Records a participant in the Engage phase.
   * 
   * @param participant - Participant to record
   */
  recordParticipant(participant: Participant): void {
    this.engage.participants.push(participant);
  }

  /**
   * Adds an item to the Gather phase.
   * 
   * @param item - Item to add
   */
  addItem(item: Item): void {
    this.gather.items.push(item);
    // Update arrange session items
    this.arrange = new ArrangeSession(
      this.gather.items,
      this.arrange.criteriaSet,
      this.arrange.toolConfig,
      this.arrange.lens
    );
  }

  /**
   * Runs an Arrange tool and returns the canonical score set.
   * 
   * @param toolName - Name of the tool to run
   * @returns Canonical score set from the tool execution
   */
  async runArrangeTool(toolName: string): Promise<CanonicalScoreSet> {
    return await this.arrange.run(toolName);
  }

  /**
   * Finalizes priorities by storing the final snapshot.
   * 
   * @param scoreSet - Final canonical score set to store
   */
  finalizePriorities(scoreSet: CanonicalScoreSet): void {
    this.prioritize.finalSnapshot = scoreSet;
  }
}


